/**
 * CreateNamespace Page
 *
 * Multi-step flow to create a namespace, invite members, and switch context
 * on success. Uses modals for loading and success states.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { useNamespace } from '../../context/namespace';
import { post } from '../../api/core';
import { ErrorMessage } from '../../components/ui/feedback/error-message.jsx';
import NamespaceSteps from '../../components/namespaces/namespace/NamespaceSteps';
import NamespaceSuccessModal from '../../components/namespaces/namespace/NamespaceSuccessModal';
import NamespaceLoadingModal from '../../components/namespaces/namespace/NamespaceLoadingModal';
import SEOHead from '../../components/shared/SEOHead';

export default function CreateNamespace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchUserNamespaces, switchNamespace } = useNamespace();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [members, setMembers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loadingModal, setLoadingModal] = useState({ isOpen: false, status: 'loading', error: null });
  const [successModal, setSuccessModal] = useState({ isOpen: false, namespaceName: '', namespaceId: null });

  // Set the current user as an admin when the component mounts
  useEffect(() => {
    if (user && members.length === 0) {
      setMembers([
        {
          email: user.email,
          role: 'admin', // This will be mapped to 'admin' permission on the server
          name: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          isCurrentUser: true
        }
      ]);
    }
  }, [user, members.length]);

  const createNamespace = async () => {
    if (!formData.name?.trim()) {
      setError('Please enter a namespace name');
      return;
    }

    setIsSubmitting(true);
    setLoadingModal({ isOpen: true, status: 'loading', error: null });

    try {
      // Map our frontend data structure to the server's expected format
      const namespaceData = {
        name: formData.name,
        description: formData.description || '',
        accountId: user.uid, // Use the user's UID as the accountId
        members: members.map(member => ({
          email: member.email,
          permission: member.role === 'read-only' ? 'readonly' : member.role, // Map 'read-only' to 'readonly'
          status: member.isCurrentUser ? 'active' : 'pending' // Current user is active, others are pending
        }))
      };

      // Make the API call to create the namespace
      const response = await post('/namespaces', namespaceData);
      
      // Refresh the namespaces list in the context
      await fetchUserNamespaces(true);
      
      // Close the loading modal and open the success modal directly
      setLoadingModal({ isOpen: false, status: 'loading', error: null });
      setSuccessModal({ 
        isOpen: true, 
        namespaceName: formData.name, 
        namespaceId: response.id 
      });
    } catch (err) {
      console.error('Error creating namespace:', err);
      setLoadingModal({
        isOpen: true,
        status: 'error',
        error: err.message || 'Failed to create namespace. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await createNamespace();
  };

  const handleRetry = () => {
    createNamespace();
  };

  const handleCloseLoadingModal = () => {
    setLoadingModal({ ...loadingModal, isOpen: false });
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal({ ...successModal, isOpen: false });
    
    // Switch to the newly created namespace
    if (successModal.namespaceName) {
      switchNamespace(successModal.namespaceName);
    }
    
    // Navigate to the app home with the new namespace active
    navigate('/app/');
  };

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-create-namespace" />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Create Namespace
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Create a new namespace for your organization or team
        </p>
      </div>

      {error && <ErrorMessage message={error} className="mb-4" />}

      {/* Progress Bar - Full width */}
      <div className="relative w-full h-1 bg-gray-200 dark:bg-gray-800 mb-6">
        <div
          className="absolute inset-y-0 left-0 bg-purple-600 dark:bg-purple-400 transition-all duration-500"
          style={{ width: `${((currentStep + 1) / 3) * 100}%` }}
        />
      </div>

      <NamespaceSteps
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        formData={formData}
        setFormData={setFormData}
        members={members}
        setMembers={setMembers}
        currentUser={user}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />

      {/* Loading Modal */}
      <NamespaceLoadingModal
        isOpen={loadingModal.isOpen}
        status={loadingModal.status}
        error={loadingModal.error}
        onClose={handleCloseLoadingModal}
        onRetry={handleRetry}
        namespaceName={formData.name}
      />

      {/* Success Modal */}
      <NamespaceSuccessModal
        isOpen={successModal.isOpen}
        onClose={handleCloseSuccessModal}
        namespaceName={successModal.namespaceName}
        namespaceId={successModal.namespaceId}
      />
    </div>
  );
} 